import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BoltIcon, StarIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

/** Shape of GET /users/usage (UsageService.getUsageSummary). */
interface UsageData {
  plan: string;
  balance: number;
  monthlyWords: number;
  cycleWords?: number;
  monthlyWordsUsed: number;
  welcomeWords: number;
  renewsAt: string | null;
  topUpBalance: number;
}

const UsageDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [usage, setUsage] = useState<UsageData | null>(null);

  useEffect(() => {
    axios
      .get('/users/usage')
      .then((res) => setUsage(res.data))
      .catch(() => setUsage(null));
  }, []);

  if (!usage) return null;

  const isFree = usage.plan === 'free';
  const cycleWords = Math.max(usage.monthlyWords, usage.cycleWords ?? 0);
  const pool = (isFree ? usage.welcomeWords : cycleWords) + usage.topUpBalance;
  const percentLeft = pool > 0 ? Math.max(0, Math.min((usage.balance / pool) * 100, 100)) : 0;
  const barColor =
    percentLeft <= 10 ? 'bg-red-500' : percentLeft <= 30 ? 'bg-yellow-500' : 'bg-primary-500';

  return (
    <div className="mb-2">
      <div className="flex items-center gap-2 px-4 py-3 bg-light-surface/50 dark:bg-dark-surface/50">
        <ArrowTrendingUpIcon className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
        <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
          {t('usage.title', 'AI words')}
        </span>
      </div>

      <div className="bg-light-bg dark:bg-dark-bg px-4 py-3 space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <BoltIcon className="w-4 h-4 text-light-text-secondary dark:text-dark-text-secondary" />
              <span className="text-sm text-light-text dark:text-dark-text">
                {t('usage.wordsLeft', 'Words left')}
              </span>
            </div>
            <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
              {usage.balance.toLocaleString()}
              {pool > 0 ? ` / ${pool.toLocaleString()}` : ''}
            </span>
          </div>
          {pool > 0 && (
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${barColor}`}
                style={{ width: `${percentLeft}%` }}
              />
            </div>
          )}
          <p className="text-xs mt-1 text-light-text-secondary dark:text-dark-text-secondary">
            {isFree
              ? t(
                  'usage.freeHint',
                  'Your free words do not refill — a plan or a top-up keeps you writing.',
                )
              : usage.renewsAt
                ? t('usage.refills', '{{words}} more on {{date}}', {
                    words: usage.monthlyWords.toLocaleString(),
                    date: new Date(usage.renewsAt).toLocaleDateString(),
                  })
                : ''}
          </p>
        </div>

        {usage.topUpBalance > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StarIcon className="w-4 h-4 text-yellow-500" />
              <span className="text-sm text-light-text dark:text-dark-text">
                {t('usage.topUpBalance', 'Top-up words')}
              </span>
            </div>
            <span className="text-sm text-light-text dark:text-dark-text font-semibold">
              {usage.topUpBalance.toLocaleString()}
            </span>
          </div>
        )}

        {percentLeft <= 30 && (
          <button
            type="button"
            onClick={() => navigate(isFree ? '/subscription' : '/credits')}
            className="text-sm text-primary-500 underline"
          >
            {isFree ? t('usage.choosePlan', 'Choose a plan') : t('usage.topUp', 'Top up')}
          </button>
        )}
      </div>
    </div>
  );
};

export default UsageDashboard;
